import { Schema, type } from "@colyseus/schema"

/**
 * Possible states of the matchmaking lobby
 */
export type MatchmakingState =
  | "gathering" // Players joining but not enough ready players
  | "ready" // Minimum ready players reached, waiting for more or timer expiration
  | "countdown" // Final countdown before game starts

/**
 * Schema class for matchmaking system in Durak
 * Handles all the state tracking for the matchmaking process
 */
export class MatchmakingSchema extends Schema {
  // Current state of the matchmaking process
  @type("string") state?: MatchmakingState = undefined

  // Timestamps for tracking various events
  @type("number") firstMinPlayersTime?: number // When minimum players was first reached
  @type("number") stateEnteredTime: number = Date.now() // When current state was entered

  // Instability tracking for detecting trolling behavior
  @type("number") instabilityCounter = 0 // Number of rollbacks from ready/countdown to gathering

  // Timer duration customization
  @type("number") shortTimerDuration?: number // Custom duration for the short timer

  /**
   * Reset the matchmaking state to empty
   */
  reset(): void {
    this.state = undefined
    this.firstMinPlayersTime = undefined
    this.instabilityCounter = 0
    this.shortTimerDuration = undefined
    this.stateEnteredTime = Date.now()
  }

  /**
   * Enter a new matchmaking state
   */
  enterState(newState: MatchmakingState): void {
    // Track rollbacks for instability counter
    if (
      (this.state === "ready" || this.state === "countdown") &&
      newState === "gathering"
    )
      this.instabilityCounter++

    this.state = newState
    this.stateEnteredTime = Date.now()
  }

  /**
   * Mark when minimum players was first reached
   */
  markMinPlayersReached(): void {
    if (!this.firstMinPlayersTime) this.firstMinPlayersTime = Date.now()
  }

  /**
   * Set a custom short timer duration
   */
  setShortTimerDuration(duration: number): void {
    this.shortTimerDuration = duration
  }

  /**
   * Check if the global force timer has expired
   */
  isForceTimerExpired(forceDuration: number): boolean {
    if (!this.firstMinPlayersTime) return false
    return Date.now() - this.firstMinPlayersTime >= forceDuration
  }
}
